import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Eye, EyeOff, Save, Undo2 } from 'lucide';

// MUI
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

// common
import Icon from 'common/components/Icon';
import SchemaForm from 'common/SchemaForm';
import type { ElementFormProps, IRawElement } from 'common/SchemaForm/utils';
import { PASSWORD } from 'config/regex';

// screen
import { getPasswordScore } from 'modules/account/detail/tabs/passwordUtils';
import { useTabsData } from 'modules/account/detail/hooks/useTabsData';

interface Passwords {
  password: string;
  passwordRetype: string;
}
type PasswordField = 'password' | 'passwordRetype';
interface PasswordFields {
  password: { toggle: boolean; strength: number };
  passwordRetype: { toggle: boolean; strength: number };
}
const defaultPasswordUtils = { toggle: false, strength: 0 };

const ChangePasswordTab = () => {
  const { password, viewOnly, setPassword, handleUpdate, toPreviousPage } = useTabsData();
  const [passwordFields, setPasswordFields] = useState<PasswordFields>({
    password: { ...defaultPasswordUtils },
    passwordRetype: { ...defaultPasswordUtils },
  });
  const { formatMessage } = useIntl();

  const handleSetPassword = (field: { target: { value: string; name: PasswordField } }) => {
    const { value, name } = field.target;
    const newState = { ...passwordFields };
    newState[name].strength = getPasswordScore(value);
    setPasswordFields(() => newState);
    setPassword({ ...password, [name]: value });
  };

  const togglePassword = (key: PasswordField) => {
    const newState = { ...passwordFields };
    newState[key].toggle = !newState[key].toggle;
    setPasswordFields(() => newState);
  };

  const notSamePasswordError = () =>
    password.password === password.passwordRetype
      ? undefined
      : formatMessage({ id: 'account.error.notSamePassword' });

  const renderField = (
    name: PasswordField
  ): ElementFormProps<Passwords, typeof name> | IRawElement<Passwords, typeof name> => ({
    mode: 'text-field',
    type: passwordFields[name].toggle ? 'text' : 'password',
    label: formatMessage({ id: `account.${name}` }),
    placeholder: formatMessage({ id: `account.${name}` }),
    propsWrapper: { tablet: 6 },
    rules: {
      required: true,
      minLength: { value: 8, message: formatMessage({ id: 'account.error.minLength' }) },
      pattern: { value: PASSWORD, message: formatMessage({ id: 'account.error.invalidPassword' }) },
      validate: notSamePasswordError,
      onChange: handleSetPassword,
    },
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            onClick={() => togglePassword(name)}
            color="primary"
            aria-label={passwordFields[name].toggle ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
          >
            <Icon icon={passwordFields[name].toggle ? EyeOff : Eye} />
          </IconButton>
        </InputAdornment>
      ),
    },
  });

  useEffect(() => {
    setPasswordFields((old) => ({
      ...old,
      password: { ...old.password, strength: getPasswordScore(password.password) },
      passwordRetype: {
        ...old.passwordRetype,
        strength: getPasswordScore(password.passwordRetype),
      },
    }));
  }, [password]);

  return (
    <SchemaForm<Passwords>
      formHookProps={{ values: password }}
      onSubmit={handleUpdate}
      globalProps={{ readOnly: viewOnly }}
      formProps={{
        sx: {
          '& .MuiGrid-root.MuiGrid-container': {
            '& .MuiGrid-root.MuiGrid-item': {
              '& .MuiGrid-root.MuiGrid-container': { display: 'flex', justifyContent: 'center' },
            },
          },
        },
      }}
      schema={{
        fields: ({ formProps: { readOnly } }) => ({
          password: renderField('password'),
          blank1: { mode: 'node' },
          passwordRetype: renderField('passwordRetype'),
          blank4: { mode: 'node' },
          back: {
            mode: 'button',
            variant: 'outlined',
            startIcon: <Icon icon={Undo2} size={16} />,
            children: formatMessage({ id: 'back' }),
            onClick: toPreviousPage,
            propsWrapper: { mobile: true, display: 'flex', justifyContent: 'flex-end' },
          },
          submit: {
            mode: 'button',
            type: 'submit',
            variant: 'contained',
            startIcon: <Icon icon={Save} size={16} />,
            hidden: readOnly,
            children: formatMessage({ id: 'save' }),
            propsWrapper: { mobile: undefined },
          },
        }),
      }}
    />
  );
};

export default ChangePasswordTab;
